/**
 * Turn off pinch and double-tap zoom — in the installed app only.
 *
 * TIM ASKED FOR THIS ON 26 AUGUST: "while in mobile app view, disable zoom gestures." The reason
 * is not hard to guess from the rest of the day: this app now has a 380px pull gesture at the top
 * of every screen and a draft board that pans under a finger, and both of those are two fingers
 * away from an accidental zoom that leaves the page sitting at 1.4× with no obvious way back.
 *
 * WHY IT IS SCOPED TO STANDALONE, and this is the one judgement in the file. `noZoomOnFocus.ts`
 * argues at length against putting `user-scalable=no` in the head, and the argument still holds:
 * somebody pinching into a sixteen-round board on draft night is a real thing managers do, and
 * taking zoom away from a whole app to stop an accident is a bad trade. So this applies where Tim
 * asked for it — the home-screen app, which is literally what "app view" means and is where the
 * accidental gesture happens, because there is no browser chrome to show you that you are zoomed
 * and no address bar to reset it from. Opened in a Safari tab the app still zooms normally, and
 * anyone who needs to magnify something has that.
 *
 * If the board turns out to need pinch on 8 September, this is one function to stop calling.
 *
 * WHAT IT TAKES TO ACTUALLY STOP IT is three things, because no one of them is enough:
 *
 *   `maximum-scale=1` in the viewport   iOS Safari has ignored this since iOS 10 for
 *                                       accessibility, so it does nothing on its own. It is here
 *                                       because it DOES work in a standalone window and on
 *                                       Android, and because it is what makes the state readable.
 *   `gesturestart` prevented            The non-standard WebKit pinch events. This is the one
 *                                       that stops two fingers on iOS.
 *   `touch-action: manipulation`        Double-tap-to-zoom, which is a separate gesture from
 *                                       pinch and survives both of the above.
 */

/** Whether the page is running as an installed app rather than in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  /* iOS Safari's own flag, which predates the media query and is the only one it honours. */
  const ios = (window.navigator as { standalone?: boolean }).standalone === true;
  const query = typeof window.matchMedia === 'function'
    && window.matchMedia('(display-mode: standalone)').matches;
  return ios || query;
}

/** The viewport content string with no room above 1. Shared with `noZoomOnFocus`'s clamp. */
export const VIEWPORT_LOCKED =
  'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';

/**
 * Wire it up. Returns an unsubscribe, and is a no-op in a browser tab or on a desktop.
 *
 * The class on `<html>` is what the stylesheet hangs `touch-action` off, and it is also how a
 * test — or a person wondering why their phone will not zoom — can see that this is on.
 */
export function installNoZoomGestures(): () => void {
  if (typeof document === 'undefined' || !isStandalone()) return () => {};

  const root = document.documentElement;
  const meta = document.querySelector('meta[name="viewport"]');
  const before = meta?.getAttribute('content') ?? null;

  root.classList.add('zoomlock');
  meta?.setAttribute('content', VIEWPORT_LOCKED);

  /*
   * `passive: false`, or `preventDefault` is ignored and the whole thing is decorative. All three
   * events are stopped rather than just `gesturestart`: Safari will start a gesture from a state
   * where only `gesturechange` fires if the first touch began before this ran.
   */
  const stop = (e: Event) => e.preventDefault();
  const events = ['gesturestart', 'gesturechange', 'gestureend'] as const;
  for (const type of events) document.addEventListener(type, stop, { passive: false });

  return () => {
    for (const type of events) document.removeEventListener(type, stop);
    root.classList.remove('zoomlock');
    if (before !== null) meta?.setAttribute('content', before);
  };
}
